/**
 * Workflow hooks using TanStack Query
 * Provides data fetching, caching, and mutations for workflow item operations
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@clerk/clerk-react";
import { useAuthContext } from "@/contexts/AuthContext";
import { useModuleGate } from "@/hooks/useAccess";
import { WORKFLOW_READ_MODULES } from "@/lib/modules";
import { WorkflowItemsService } from "@/api/services/WorkflowItemsService";
import type {
  WorkflowItemListParams,
  WorkflowItemResponse,
} from "@/types/workflow";
import type { workflows_WorkflowItemStatsResponse } from "@/api/models/workflows_WorkflowItemStatsResponse";
import { parseChangeRequestPayload } from "@/types/changeRequest";
import { ApiError } from "@/api";
import { alreadyHandledMessage, isAlreadyHandledError } from "@/lib/alreadyHandled";
import { mapLimited } from "@/lib/mapLimited";
import { retryUnlessClientError } from "@/lib/queryRetry";
import { errorStatus } from "@/lib/translateBackendError";
import { toast } from "sonner";
import { useLingui } from "@lingui/react";
import { NAV_BADGE_POLL_MS, visiblePoll } from "@/lib/polling";
import { endpointKey } from "@/lib/queryKeys";
import { fetchWorkflowItems } from "@/hooks/workflowItemsQuery";

// Query keys for workflow items
export const workflowKeys = {
  all: ["workflow-items"] as const,
  lists: () => [...workflowKeys.all, "list"] as const,
  list: (params: WorkflowItemListParams) =>
    [...workflowKeys.lists(), params] as const,
  details: () => [...workflowKeys.all, "detail"] as const,
  detail: (id: string) => [...workflowKeys.details(), id] as const,
  pendingCount: () => [...workflowKeys.all, "pending-count"] as const,
  // Prefix of GET /workflow-items/stats (endpointKey). Invalidated next to
  // pendingCount() by every write that moves the badge (#2194 A2).
  stats: () => [...workflowKeys.all, "stats"] as const,
};

/**
 * Did someone else get to the workflow item first? Items are assigned to a ROLE
 * and rendered on several surfaces at once (web inbox, mobile approvals), so two
 * admins racing the same action is ordinary. The backend answers:
 *   422 → ErrWorkflowItemCompleted   (the common sequential race)
 *   409 → ErrWorkflowOptimisticLock  (true simultaneous writes)
 *   404 → the item is gone
 */
export function isWorkflowAlreadyHandled(error: unknown): boolean {
  return isAlreadyHandledError(error) || errorStatus(error) === 422;
}

/**
 * Hook to fetch paginated workflow items with filters.
 */
export function useWorkflowItems(params: WorkflowItemListParams = {}) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: workflowKeys.list(params),
    // Board + change_request triage group/filter client-side and need the FULL
    // set (`fetchAll`); everything else reads one server page.
    queryFn: () => fetchWorkflowItems(params),
    enabled: isSignedIn && isAuthReady,
    staleTime: 30000,
    retry: retryUnlessClientError,
  });
}

/**
 * Hook to fetch a single workflow item by ID with transition history.
 */
export function useWorkflowItem(id: string, options?: { enabled?: boolean }) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: workflowKeys.detail(id),
    queryFn: () => WorkflowItemsService.getApiV1WorkflowItems1(id),
    enabled: (options?.enabled ?? true) && !!id && isSignedIn && isAuthReady,
    staleTime: 30000,
  });
}

/**
 * Hook to fetch count of pending workflow items.
 */
export function usePendingCount(options?: { enabled?: boolean }) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();
  const canReadWorkflows = useModuleGate(WORKFLOW_READ_MODULES);

  return useQuery({
    queryKey: workflowKeys.pendingCount(),
    queryFn: () => WorkflowItemsService.getApiV1WorkflowItemsPendingCount(),
    enabled:
      (options?.enabled ?? true) &&
      isSignedIn &&
      isAuthReady &&
      canReadWorkflows,
    staleTime: 15000,
    ...visiblePoll(NAV_BADGE_POLL_MS),
    retry: retryUnlessClientError,
  });
}

/**
 * Hook to transition a workflow item (fire an FSM action).
 */
export function useTransition() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: ({
      id,
      action,
      comment,
      parent_note,
    }: {
      id: string;
      action: string;
      comment?: string;
      /** Reason the requesting parent sees; the server accepts it only with reject (#1716). */
      parent_note?: string;
      /** Suppress the generic success toast (e.g. when the caller shows its own). */
      silent?: boolean;
    }) =>
      WorkflowItemsService.postApiV1WorkflowItemsTransition(id, {
        action,
        comment,
        parent_note,
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.pendingCount() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.stats() });
      queryClient.invalidateQueries({
        queryKey: workflowKeys.detail(variables.id),
      });
      if (variables.silent) return;
      toast.success(
        i18n._({
          id: "workflow.toast.transitionSuccess",
          message: "Action completed successfully",
        }),
      );
    },
    onError: (error: Error, variables) => {
      // Someone got there first: nothing is actionable, so resettle the row and
      // the badge against the truth and say so calmly. A 500 still surfaces as
      // a real failure.
      if (isWorkflowAlreadyHandled(error)) {
        queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
        queryClient.invalidateQueries({
          queryKey: workflowKeys.pendingCount(),
        });
        queryClient.invalidateQueries({
          queryKey: workflowKeys.stats(),
        });
        queryClient.invalidateQueries({
          queryKey: workflowKeys.detail(variables.id),
        });
        toast.info(i18n._(alreadyHandledMessage));
        return;
      }
      // Four-eyes rule (#1418): the person who raised a change request cannot
      // approve it, so say why instead of a generic failure. A request an admin
      // sent while viewing the app as a parent is exempt.
      if (errorStatus(error) === 403 && isSelfApprovalError(error)) {
        toast.error(
          i18n._({
            id: "workflow.transition.selfApproval",
            message:
              "You raised this request, so another admin must approve it.",
          }),
        );
        return;
      }
      // coverage_breach: acknowledge and dismiss need a comment. The dialog
      // enforces it, so this only shows if a blank one still reached the server.
      if (isCommentRequiredError(error)) {
        toast.error(
          i18n._({
            id: "workflow.transition.commentRequired",
            message: "A comment is required for this action.",
          }),
        );
        return;
      }
      toast.error(
        i18n._({
          id: "workflow.toast.error",
          message: "Failed to perform action",
        }),
      );
    },
  });
}

/**
 * Hand a workflow item to an employee (`employeeId`), or take the person off
 * it (`employeeId` empty). The role the item was raised for stays; the change
 * lands in the item's timeline.
 */
export function useAssignWorkflowItem() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: ({
      id,
      employeeId,
      comment,
    }: {
      id: string;
      employeeId: string;
      comment?: string;
    }) =>
      WorkflowItemsService.postApiV1WorkflowItemsAssign(id, {
        employee_id: employeeId,
        comment,
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.stats() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.detail(variables.id) });
      toast.success(
        variables.employeeId
          ? i18n._({ id: "workflow.assign.toast.assigned", message: "Handed over" })
          : i18n._({ id: "workflow.assign.toast.unassigned", message: "Person removed" }),
      );
    },
    onError: (error: Error, variables) => {
      // The assignee codes are 422s too, so they are read before the
      // already-handled check below.
      const code = errorCode(error);
      if (code === "WORKFLOW_ASSIGNEE_CANNOT_SEE") {
        toast.error(
          i18n._({
            id: "workflow.assign.error.cannotSee",
            message: "Only admins can see this. Hand it to an admin.",
          }),
        );
        return;
      }
      if (code === "WORKFLOW_ASSIGN_FORBIDDEN") {
        toast.error(
          i18n._({
            id: "workflow.assign.error.forbidden",
            message: "You can only take this or hand it to the group lead.",
          }),
        );
        return;
      }
      if (code === "WORKFLOW_ASSIGNEE_NO_LOGIN") {
        toast.error(
          i18n._({
            id: "workflow.assign.error.noLogin",
            message: "Without a login, this person cannot take items.",
          }),
        );
        return;
      }
      if (code === "WORKFLOW_ASSIGNEE_INACTIVE" || code === "WORKFLOW_ASSIGNEE_NOT_FOUND") {
        toast.error(
          i18n._({
            id: "workflow.assign.error.unavailable",
            message: "This person cannot take items at the moment.",
          }),
        );
        return;
      }
      if (isWorkflowAlreadyHandled(error)) {
        queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
        queryClient.invalidateQueries({ queryKey: workflowKeys.stats() });
        queryClient.invalidateQueries({ queryKey: workflowKeys.detail(variables.id) });
        toast.info(i18n._(alreadyHandledMessage));
        return;
      }
      toast.error(
        i18n._({ id: "workflow.assign.error", message: "Could not hand over the item" }),
      );
    },
  });
}

/**
 * Send a reminder to whoever holds a workflow item (the person, else the group
 * lead). Server-side rights: admin or the item's group lead.
 */
export function useRemindWorkflowItem() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  const invalidate = (id: string) => {
    queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
    queryClient.invalidateQueries({ queryKey: workflowKeys.stats() });
    queryClient.invalidateQueries({ queryKey: workflowKeys.detail(id) });
  };

  return useMutation({
    mutationFn: ({ id, comment }: { id: string; comment?: string }) =>
      WorkflowItemsService.postApiV1WorkflowItemsRemind(id, { comment }),
    onSuccess: (_data, variables) => {
      invalidate(variables.id);
      toast.success(i18n._({ id: "workflow.remind.toast.sent", message: "Reminder sent" }));
    },
    onError: (error: Error, variables) => {
      // The reminder codes are 422s too, so they are read before the
      // already-handled check below.
      const code = errorCode(error);
      // RemindDialog shows this one inline under the recipient and disables Send.
      if (code === "WORKFLOW_REMINDER_TOO_SOON") return;
      if (code === "WORKFLOW_REMIND_FORBIDDEN") {
        toast.error(
          i18n._({
            id: "workflow.remind.error.forbidden",
            message: "Only an admin or the group lead can send a reminder",
          }),
        );
        return;
      }
      if (code === "WORKFLOW_NO_HOLDER") {
        toast.error(
          i18n._({
            id: "workflow.remind.error.noHolder",
            message: "No one has taken this yet, so there is nobody to remind",
          }),
        );
        return;
      }
      if (isWorkflowAlreadyHandled(error)) {
        invalidate(variables.id);
        toast.info(i18n._(alreadyHandledMessage));
        return;
      }
      toast.error(i18n._({ id: "workflow.remind.error", message: "Could not send the reminder" }));
    },
  });
}

/** The `code` of an API error body, or undefined. */
function errorCode(error: Error): string | undefined {
  if (!(error instanceof ApiError)) return undefined;
  const body: unknown = error.body;
  if (typeof body !== "object" || body === null) return undefined;
  const code = (body as { code?: unknown }).code;
  return typeof code === "string" ? code : undefined;
}

/** The server's blank-comment refusal (`WORKFLOW_COMMENT_REQUIRED`). */
function isCommentRequiredError(error: Error): boolean {
  if (!(error instanceof ApiError)) return false;
  const body: unknown = error.body;
  return (
    typeof body === "object" &&
    body !== null &&
    (body as { code?: unknown }).code === "WORKFLOW_COMMENT_REQUIRED"
  );
}

/** The server's four-eyes refusal (`WORKFLOW_SELF_APPROVAL`). */
function isSelfApprovalError(error: Error): boolean {
  if (!(error instanceof ApiError)) return false;
  const body: unknown = error.body;
  return (
    typeof body === "object" &&
    body !== null &&
    (body as { code?: unknown }).code === "WORKFLOW_SELF_APPROVAL"
  );
}

export interface BatchApproveSummary {
  /** Approved and applied (the item reached `executed` with a clean result). */
  applied: string[];
  /**
   * Approved, but the change did not apply in full: the item stopped short of
   * `executed`, or reached it with an `error` or `partial` result.
   */
  notApplied: string[];
  /** The server refused the approve. */
  failed: string[];
  /** Someone else handled the item first (409 or 422, or it is gone): nothing to do. */
  alreadyHandled: string[];
}

// The change is applied after the approve commits, and an apply error does not
// fail the approve call, so only a re-read tells the two apart.
const APPLIED_STATE = "executed";

/**
 * Did the approved item's change apply in full? The router lands a 0-applied
 * result with no executor error in `executed` too, carrying
 * `result.status="error"` (workflow-engine.md), so the state alone is not enough.
 */
function changeApplied(item: WorkflowItemResponse): boolean {
  if (item.current_state !== APPLIED_STATE) return false;
  // Anything but `ok` needs a look, `partial` included. Mobile still counts
  // `executed` as applied (follow-up, DECISIONS-WEB W11).
  const status = parseChangeRequestPayload(item.payload)?.executor?.result?.status;
  return !status || status === "ok";
}

/**
 * Hook to approve a batch of change_request items. Fires one `approve` transition
 * per item (the server cascades execute + apply via the router; NO standalone
 * `process` call), three at a time, then re-reads each approved item to learn
 * whether its change applied. Invalidates the lists + pending count once on
 * settle. The caller reports the outcome: it knows the items by name.
 */
export function useBatchApprove() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ids: string[]): Promise<BatchApproveSummary> => {
      const applied: string[] = [];
      const notApplied: string[] = [];
      const failed: string[] = [];
      const alreadyHandled: string[] = [];
      const approveOne = async (id: string) => {
        try {
          await WorkflowItemsService.postApiV1WorkflowItemsTransition(id, { action: "approve" });
        } catch (error) {
          // Same reading as the single transition: a raced item is not a problem.
          if (isWorkflowAlreadyHandled(error)) alreadyHandled.push(id);
          else failed.push(id);
          return;
        }
        try {
          const item = await WorkflowItemsService.getApiV1WorkflowItems1(id);
          if (changeApplied(item)) applied.push(id);
          else notApplied.push(id);
        } catch {
          // The approve committed; an unreadable outcome is not a failure.
          applied.push(id);
        }
      };
      await mapLimited(ids, 3, approveOne);
      // Keep the input order so the report lists items as the operator saw them.
      const order = (list: string[]) => ids.filter((id) => list.includes(id));
      return {
        applied: order(applied),
        notApplied: order(notApplied),
        failed: order(failed),
        alreadyHandled: order(alreadyHandled),
      };
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.pendingCount() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.stats() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.details() });
    },
  });
}

/** The inbox counts of a `GET /workflow-items/stats` response. */
export function workflowCountsFromStats(stats: workflows_WorkflowItemStatsResponse | undefined) {
  return {
    pendingCount: stats?.pending ?? 0,
    overdueCount: stats?.overdue ?? 0,
    dueTodayCount: stats?.due_today ?? 0,
    myTasksCount: stats?.my_tasks ?? 0,
  };
}

/**
 * The workflow inbox counts (pending, overdue, due today, my tasks) from
 * `GET /workflow-items/stats`: one small response computed over every item the
 * caller can see, instead of counting the first 200 items client-side (#2194
 * A2). "Due today" is today's date in the organisation's time zone.
 */
const WORKFLOW_STATS_PATH = "/api/v1/workflow-items/stats";

export function useWorkflowStats(options?: { enabled?: boolean }) {
  const { isSignedIn } = useAuth();
  const { isAuthReady, orgId } = useAuthContext();

  const stats = useQuery({
    queryKey: endpointKey(orgId, WORKFLOW_STATS_PATH),
    queryFn: () => WorkflowItemsService.getApiV1WorkflowItemsStats(),
    enabled: (options?.enabled ?? true) && isSignedIn && isAuthReady,
    staleTime: 30000,
    refetchInterval: 60000,
  });

  return {
    ...workflowCountsFromStats(stats.data),
    isLoading: stats.isLoading,
    // A failed read is not "0 pending": callers show a dash and a retry.
    isError: stats.isError,
    error: stats.error,
    refetch: stats.refetch,
  };
}
